import { Component, OnInit, Input, ViewEncapsulation, Output, EventEmitter } from "@angular/core";
import { 
  Icons, 
  GridOptions, 
  type ColDef, 
  GridApi, 
  ICellRendererParams, 
  GridReadyEvent, 
  GetRowIdFunc, 
  GetRowIdParams, 
  ValueFormatterParams, 
  RowClassRules, 
  ITextFilterParams, 
  themeQuartz,
} from "ag-grid-enterprise";
import { ActionMenuCellRendererParams, AgActionMenuCellRendererComponent } from "src/app/angular-elements/shared/components/ag-grid-cell-renderers/ag-action-menu-cell-renderer/ag-action-menu-cell-renderer.component";
import { AgQuickActionsCellRendererComponent } from "src/app/angular-elements/shared/components/ag-grid-cell-renderers/ag-quick-actions-cell-renderer/ag-quick-actions-cell-renderer.component";
import { AgCustomDropdownCellRendererComponent } from "src/app/angular-elements/shared/components/ag-grid-cell-renderers/ag-custom-dropdown-cell-renderer/ag-custom-dropdown-cell-renderer.component";
import { RowDataStructure, SepAppInfoStructure, EmittedStatusObj, DocumentStore } from "./interfaces/sep-application-review-grid.interface";
import { SepApplicationReviewDashboardService } from "../services/sep-application-review-dashboard.service";
import { ActionItem, ActionItemRendererParams, DetailObject } from "src/app/angular-elements/shared/components/ag-grid-cell-renderers/interfaces/ag-grid-cell-renderer.interface";
import { AgDetailNotesRendererComponent, DetailNotesRendererParams } from "src/app/angular-elements/shared/components/ag-grid-cell-renderers/ag-detail-notes-renderer/ag-detail-notes-renderer.component";
import { SEP_REVIEW_DASHBOARD_COLUMN_NAME, STATUS_OPTION_DISPLAY_VALUES, STATUS_OPTION_SERVICE_VALUES, STATUS_OPTIONS_DETAILS } from "./enums/sep-application-review-grid.enum";
import { BooleanInput } from "@ptl/global-components";
import { DisplayableFile } from "src/app/angular-elements/form-wrappers/form-components/required-docs-upload/interfaces/displayable-file.interface";
import moment from 'moment';
import { environment } from "src/environments/environment";
import { MomentTimeZoneEnum } from "src/app/shared/enums/moment-timezone.enum";
import { EnrollPayloadRequest } from "src/app/angular-elements/shared/interfaces/enrollment-submission.interface";
@Component({
  selector: "app-sep-application-review-grid",
  templateUrl: "./sep-application-review-grid.component.html",
  styleUrl: "./sep-application-review-grid.component.scss",
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class SepApplicationReviewGridComponent implements OnInit {
  @Input() rowData: RowDataStructure[];
  @Input() searchAppServiceResponse: SepAppInfoStructure[];
  @Input() @BooleanInput() isNotStatusTab: boolean = false;

  @Output() statusChanged: EventEmitter<EmittedStatusObj>
  = new EventEmitter<EmittedStatusObj>();

  @Output() selectedRows: EventEmitter<any>
  = new EventEmitter<any>();

  public gridApi: GridApi;

  constructor(
    private sepDashboardService: SepApplicationReviewDashboardService,
    private sepApplicationReviewDashboardService: SepApplicationReviewDashboardService
  ){}

  public getRowId: GetRowIdFunc = (params: GetRowIdParams) => {
    return params.data.applicationNumber; 
  };

  icons: Icons = {
    richSelectOpen: `<span class="material-symbols-outlined together-blue">keyboard_arrow_up</span>`
  }

  rowClassRules: RowClassRules ={
    'disable-submitted': (params) => { 
      return params.data.status == STATUS_OPTION_SERVICE_VALUES.SUBMITTED;
    }
  };

  individualAllowedExtensions = [
    //image types
    '.png',
    '.jpeg',
    '.jpg',
    //document types
    '.doc',
    '.docx',
    '.pdf'
  ]

  masterDetail: boolean = true;

  colDefs: any = 
  [
    { 
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.ACTION_MENU,
      filter: false,
      headerName: "Action Menu",
      editable: false,
      cellRenderer: AgActionMenuCellRendererComponent,
      cellRendererParams:{
        backgroundColor: '#0078C1',
        getActionItems: (params) => {
          return this.getMenuItems(params)
        }
      },
      maxWidth: 100,
      sortable: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      resizable: false
    },
    { 
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.STATUS,
      headerName: "Status",
      editable: (
        params: {
          api: GridApi,
          colDef: unknown,
          column: unknown,
          context: unknown,
          data: { status: STATUS_OPTION_SERVICE_VALUES },
          node: unknown
        }) => {
        // params.data will give you the entire row object
        // params.value will give you the current value of the 'status' field for this cell
        return params.data.status !== STATUS_OPTION_SERVICE_VALUES.SUBMITTED;
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: (params) => {
          return STATUS_OPTION_DISPLAY_VALUES[params.value];
        },
      },
      cellRenderer: AgCustomDropdownCellRendererComponent,
      cellRendererParams: {
        customDropdownSelections: STATUS_OPTIONS_DETAILS
      },
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: [
          STATUS_OPTION_SERVICE_VALUES.NOT_STARTED,
          STATUS_OPTION_SERVICE_VALUES.PENDING,
          STATUS_OPTION_SERVICE_VALUES.APPROVED,
          STATUS_OPTION_SERVICE_VALUES.DENIED
        ],
        cellRenderer: AgCustomDropdownCellRendererComponent,
        cellRendererParams: {
          customDropdownSelections: STATUS_OPTIONS_DETAILS
        }
      },
      onCellValueChanged: async (params) => {
        await this.sepDashboardService.updateNotesOrStatus(params, params.newValue, SEP_REVIEW_DASHBOARD_COLUMN_NAME.STATUS, this.gridApi)
        this.statusChanged.emit(
          {
            oldValue: params.oldValue,
            newValue: params.newValue
          }
        );
      },
      headerStyle: { 'text-align': 'center'},
      wrapText: false
    },
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.APPLICATION_NUMBER,
      filterParams: {
        filterOptions: ['contains']
      } as ITextFilterParams,
      headerName: "Application Number",
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: false
    },
    { 
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.APPLICATION_DATE,
      headerName: "Application Date",
/*       filter: 'agDateColumnFilter',
      filterParams: {
        browserDatePicker: false,
      } as IDateFilterParams,
      valueFormatter: (appDate: ValueFormatterParams<any, any>) => {
        return appDate.value.toDateString();
      }, */
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: false
    },
    { 
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.SUBSCRIBER_NAME,
      headerName: "Subscriber Name",
      filterParams: {
        filterOptions: ['contains']
      } as ITextFilterParams,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: false
    },
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.LIFE_EVENT,
      filter: 'agSetColumnFilter',
      headerName: "Life Event",
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: false
    },
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.SUBSCRIBER_DOB,
      headerName: "Date Of Birth",
/*       cellDataType: "dateString",
      filter: 'agDateColumnFilter',
      filterParams: {
        browserDatePicker: false,
      } as IDateFilterParams, */
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: false
    },
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.LAST_EDITED,
/*       cellDataType: 'dateTimeString',
      filter: 'agDateColumnFilter',
      filterParams: {
        browserDatePicker: false,
        includeTime: true
      } as IDateFilterParams, */
      headerName: "Last Edited",
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: false,
      valueFormatter: (lastEditTime: ValueFormatterParams<any, string>) => {
        // moment.tz() says "I have a date and *I* know the timzeone"
        // say timezone is NY THEN change it to the build config
        const val = moment.tz(lastEditTime.value, "MM/DD/YYYY hh:mm:ss", "America/New_York").tz(MomentTimeZoneEnum[environment.configs.siteTimeZone]).format("MM/DD/YYYY hh:mm A z");
        return val;
      }
    },
    { 
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.LAST_EDITED_BY,
      headerName: "Last Edited By",
      filterParams: {
        filterOptions: ['contains']
      } as ITextFilterParams,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: false
    },
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.DOCUMENT_ACTIONS,
      headerName: "Actions",
      cellRenderer: AgQuickActionsCellRendererComponent,
      cellRendererParams: {
        getActionItems: (params) => this.getQuickActionItems(params)
      },
      sortable: false,
      resizable: false,
      filter: false,
      wrapText: false
    },
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.NOTES, 
      hide: true, 
      suppressColumnsToolPanel: true,
      filter: false
    }, 
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.DOCUMENTS,
      hide: true, 
      suppressColumnsToolPanel: true,
      cellDataType: 'object',
      valueFormatter: (docObj: ValueFormatterParams<any, DocumentStore>) => docObj.value,
      filter: false
    },
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.DETAIL_DISPLAY_OBJECT,
      hide: true,
      suppressColumnsToolPanel: true,
      cellDataType: 'object',
      valueFormatter: (docObj: ValueFormatterParams<any, DetailObject>) => docObj.value,
      filter: false
    },
    {
      field: SEP_REVIEW_DASHBOARD_COLUMN_NAME.ENROLLMENT_OBJ,
      hide: true,
      suppressColumnsToolPanel: true,
      cellDataType: 'object',
      valueFormatter: (enrollmentObj: ValueFormatterParams<any, EnrollPayloadRequest>) => enrollmentObj.value,
      filter: false
    },
  ]

  defaultColDef: ColDef = {
    flex: 1,
    filter: 'agTextColumnFilter',
    filterParams: {
      maxNumConditions: 1,
      filterOptions: ['contains']
    } as ITextFilterParams,
    autoHeight: true,
    resizable: true,
    wrapHeaderText: false,
    autoHeaderHeight: false,
    wrapText: true,
    cellStyle: {
      'display':'flex',
      'align-items':'center', 
      'justify-content': 'flex-start', 
      'line-height': 1.5,
      'overflow': 'hidden',
      'text-overflow': 'ellipsis'
    },
    floatingFilter: true
  };
//TODO: Enhance Date Filters to utilize actual date formatting rather than strings
/* 
  dataTypeDefinitions: DataTypeDefinitions = {
    dateString: {
      baseDataType: 'dateString',
      extendsDataType: 'dateString',
      valueParser: (
        params: ValueParserLiteParams<string, string>,
      ) =>  {
        return params.newValue != null && params.newValue.match("\\d{2}/\\d{2}/\\d{4}") ? params.newValue : null;
      },
      valueFormatter: (
        params: ValueFormatterLiteParams<string, string>
      ) => {
        return params.value == null ? "" : params.value;
      },
      dataTypeMatcher: (value: any) => 
        {
          return typeof value === "string" && !!value.match("\\d{2}/\\d{2}/\\d{4}")
        },
      dateParser: (value: string | undefined) => {
        if(value == null || value === "") {
          return undefined;
        }
        const dateParts = value.split("/");
        return dateParts.length === 3 ? 
        new Date( +dateParts[2], +dateParts[0] - 1, +dateParts[1]) : undefined;
      },
      dateFormatter: (value: Date | undefined) => {
        if(value == null){
          return undefined;
        }
        return `${String(value.getMonth()+1)}/${String(value.getDate())}/${String(value.getFullYear())}`
        //return moment(value).format('MM/DD/YYYY');
      }
    } as DateStringDataTypeDefinition
  }
 */
  gridOptions: GridOptions<any>;

  ngOnInit(): void {
    this.gridOptions = {
      theme: themeQuartz.withParams({
        rowBorder: { style: 'solid', width: 1, color: '#666666' }
     }),
      getRowId: this.getRowId,
      rowClassRules: this.rowClassRules,
      popupParent: document.body,
      columnDefs: this.colDefs,
      rowData: this.rowData,
      //dataTypeDefinitions: this.dataTypeDefinitions,
      defaultColDef: this.defaultColDef,
      icons: this.icons, 
      singleClickEdit: true,
      masterDetail: true,
      detailRowAutoHeight: true,
      detailCellRenderer: AgDetailNotesRendererComponent,
      detailCellRendererParams: {
          getActionItems: (rowData) => this.getDetailButtonConfigs(rowData),
          getDetailValues: (params) => params.node.data.detailDisplayObj,
          getNotesValue: (params) => params.node.data?.notes
      },
      rowSelection: {
        mode: 'multiRow',
        selectAll: 'currentPage',
        isRowSelectable: (rowNode) => rowNode.data ? rowNode.data.status === STATUS_OPTION_SERVICE_VALUES.APPROVED : false,
        hideDisabledCheckboxes: true,
        enableClickSelection: true,
        headerCheckbox: true
      },
      onSelectionChanged: () => {
        const selectedValues = this.gridApi.getSelectedRows();
        this.selectedRows.emit(selectedValues);
      },
      pagination: true,
      paginationPageSize: 20,
      paginationPageSizeSelector: [20, 50, 100]
    };
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.sepApplicationReviewDashboardService.syncGrid(this.gridApi);
  }

  getDetailButtonConfigs(rowData){
    if(!rowData) return [];
    const editAndSaveButtonConfigs = [
      {
          id: 'editNotes',
          label: 'Edit',
          icon: 'edit',
          callback: null
      },
      {
        id: 'saveNotes',
        label: 'Save',
        icon: 'save',
        callback: async (rowData: RowDataStructure, params: DetailNotesRendererParams, newNotesValue: string) => {
          return await this.sepDashboardService.updateNotesOrStatus(params, newNotesValue, SEP_REVIEW_DASHBOARD_COLUMN_NAME.NOTES, this.gridApi)
        }
      }
    ]
    return editAndSaveButtonConfigs;
  }


  getMenuItems(params: ActionMenuCellRendererParams): ActionItem[] {
    if (!params) return [];

    const actions: ActionItem[] = [
      {
        id: "viewNotes",
        label: "View/Edit Notes",
        icon: "assignment",
        callback: (
          rowData: RowDataStructure,
          params: ActionItemRendererParams | ICellRendererParams,
          actionItem: ActionItem,
        ) =>
          this.sepDashboardService.handleMenuAction(
            rowData,
            params,
            actionItem,
          ),
      },
      {
        id: "viewDocuments",
        label: "View Documents",
        icon: "visibility",
        disableAction: !this.canViewDocuments(params.data.documents?.files),
        callback: (
          rowData: RowDataStructure,
          params: ActionItemRendererParams | ICellRendererParams,
          actionItem: ActionItem,
        ) => {
          return this.sepDashboardService.handleMenuAction(
            rowData,
            params,
            actionItem,
            this.searchAppServiceResponse,
          )
        }
      },
      {
        id: "downloadApplication",
        label: "View Application",
        icon: "local_library",
        callback: (
          rowData: RowDataStructure,
          params: ActionItemRendererParams | ICellRendererParams,
          actionItem: ActionItem,
        ) =>
          this.sepDashboardService.handleMenuAction(
            rowData,
            params,
            actionItem
          ),
      },
      {
        id: "uploadDocuments",
        label: "Upload Documents",
        icon: "upload_file",
        disableAction: !this.canUploadDocuments(params.data.status),
        callback: (
          rowData: RowDataStructure, // This will be stale, so below we fetch a fresh instance to have updated data
          params: ActionItemRendererParams | ICellRendererParams,
          actionItem: ActionItem,
        ) => {
          // 1. Get the unique ID from the potentially stale rowData
          const targetId = rowData.applicationNumber; 
          
          // 2. Fetch the absolute freshest data snapshot from the Grid API
          const freshNode = this.gridApi.getRowNode(targetId);
          const freshRowData = freshNode ? freshNode.data : rowData;

          // 3. Pass the fresh data down to your service
          return this.sepDashboardService.handleMenuAction(
            freshRowData,
            params,
            actionItem,
            null,
            this.gridApi
          )
        }
      },
      {
        id: "submitApplication",
        label: "Submit Application",
        icon: "send",
        disableAction: !this.canSubmitApplication(params.data.status),
        callback: async (
          rowData: RowDataStructure,
          params: ActionItemRendererParams | ICellRendererParams,
          actionItem: ActionItem
        ) => {
          await this.sepApplicationReviewDashboardService.submitApplication(params, rowData, this.gridApi);
        },
      },
    ];

    return actions
  }
 
  
  getQuickActionItems(params: ICellRendererParams): ActionItem[] {
    if (!params) return [];

    const quickActions: ActionItem[] = [
      { 
        id: 'viewDocuments',
        toolTip: 'View Documents',
        icon: 'visibility',
        disableAction: !this.canViewDocuments(params.data.documents?.files),
        callback: (
          rowData: RowDataStructure,
          params: ActionItemRendererParams | ICellRendererParams,
          actionItem: ActionItem
        ) =>  this.sepDashboardService.handleQuickAction(rowData, params, actionItem, this.searchAppServiceResponse)
      },
      { 
        id: 'viewNotes',
        toolTip: 'View/Edit Notes',
        icon: 'assignment',
        callback: (
          rowData: RowDataStructure,
          params: ActionItemRendererParams | ICellRendererParams,
          actionItem: ActionItem
        ) => this.sepDashboardService.handleQuickAction(rowData, params, actionItem) } 
      ];

    return quickActions;
  }

  canSubmitApplication(status: STATUS_OPTION_SERVICE_VALUES): boolean {
    return (
      status === STATUS_OPTION_SERVICE_VALUES.APPROVED
    );
  }

  canUploadDocuments(status: STATUS_OPTION_SERVICE_VALUES): boolean {
    return (
      status !== STATUS_OPTION_SERVICE_VALUES.SUBMITTED
    )
  }

  canViewDocuments(documents: DisplayableFile[]): boolean {
    return (
      documents?.length > 0
    )
  }
}